/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 */
import {
  Zap,
  Shield,
  Store,
  BarChart3,
  Puzzle,
  Sparkles,
  UserPlus,
  LayoutTemplate,
  Package,
  Rocket,
} from "lucide-react";

export const FEATURES = [
  {
    icon: Zap,
    titleAr: "سرعة فائقة",
    titleEn: "Blazing Performance",
    descAr: "لوحة تحكم خفيفة ومتاجر سريعة التحميل لتبقي عملاءك داخل التجربة.",
    descEn: "A lightweight dashboard and fast storefronts that keep customers in the flow.",
  },
  {
    icon: Shield,
    titleAr: "أمان متقدم",
    titleEn: "Advanced Security",
    descAr: "حماية للبيانات والجلسات مع أفضل ممارسات الأمان للمنصات السحابية.",
    descEn: "Data and session protection aligned with modern cloud security practices.",
  },
  {
    icon: Store,
    titleAr: "إدارة متعددة للمتاجر",
    titleEn: "Multi-Store Control",
    descAr: "أدِر أكثر من متجر من مكان واحد، مع صلاحيات وتنظيم واضح.",
    descEn: "Run multiple stores from one place with clear roles and organization.",
  },
  {
    icon: BarChart3,
    titleAr: "تقارير احترافية",
    titleEn: "Pro Analytics",
    descAr: "مبيعات، أرباح، وطلبات — رؤى واضحة تساعدك على اتخاذ القرار.",
    descEn: "Sales, profits, and orders — clear insights that drive better decisions.",
  },
  {
    icon: Puzzle,
    titleAr: "تكاملات مرنة",
    titleEn: "Flexible Integrations",
    descAr: "ادفع عبر بوابات عراقية وربط خدماتك بسهولة ضمن سير عملك.",
    descEn: "Connect Iraqi payment gateways and tools into your daily workflow.",
  },
  {
    icon: Sparkles,
    titleAr: "سهولة الاستخدام",
    titleEn: "Effortless UX",
    descAr: "واجهة عربية أولاً، بسيطة بما يكفي للمبتدئ وقوية بما يكفي للمحترف.",
    descEn: "Arabic-first interface — simple for beginners, powerful for pros.",
  },
];

export const DOMAINS = [
  { nameAr: "عبايات وأزياء", nameEn: "Fashion & Abayas", emoji: "👗", span: "wide" },
  { nameAr: "المنتجات الرقمية", nameEn: "Digital Products", emoji: "🎮", span: "tall" },
  { nameAr: "الصحة واللياقة", nameEn: "Health & Fitness", emoji: "💪", span: "normal" },
  { nameAr: "إلكترونيات", nameEn: "Electronics", emoji: "💻", span: "normal" },
  { nameAr: "المجوهرات", nameEn: "Jewelry", emoji: "💎", span: "wide" },
  { nameAr: "العناية والتجميل", nameEn: "Beauty & Care", emoji: "✨", span: "normal" },
  { nameAr: "مستلزمات المنزل", nameEn: "Home Essentials", emoji: "🏠", span: "tall" },
  { nameAr: "الخدمات", nameEn: "Services", emoji: "⚙️", span: "normal" },
];

export const STATS = [
  { key: "stores", value: 1200, suffix: "+", labelAr: "متجر نشط", labelEn: "Active Stores" },
  { key: "users", value: 8500, suffix: "+", labelAr: "تاجر ومستخدم", labelEn: "Merchants & Users" },
  { key: "orders", value: 95000, suffix: "+", labelAr: "طلب مُعالَج", labelEn: "Orders Processed" },
  { key: "satisfaction", value: 98, suffix: "%", labelAr: "رضا العملاء", labelEn: "Customer Satisfaction" },
];

export const STEPS = [
  {
    icon: UserPlus,
    titleAr: "إنشاء الحساب",
    titleEn: "Create Account",
    descAr: "سجّل خلال دقائق وابدأ رحلتك في مدار المجرة.",
    descEn: "Sign up in minutes and enter the Madar galaxy.",
  },
  {
    icon: LayoutTemplate,
    titleAr: "إنشاء المتجر",
    titleEn: "Build Your Store",
    descAr: "اختر القالب والمجال، وخصّص الهوية البصرية لمتجرك.",
    descEn: "Pick a theme and niche, then brand your storefront.",
  },
  {
    icon: Package,
    titleAr: "إضافة المنتجات",
    titleEn: "Add Products",
    descAr: "ارفع منتجاتك، حدّد الأسعار والمخزون، وجهّز العروض.",
    descEn: "Upload products, set prices and stock, and prep offers.",
  },
  {
    icon: Rocket,
    titleAr: "بدء البيع",
    titleEn: "Start Selling",
    descAr: "انشر متجرك واستقبل الطلبات والمدفوعات فوراً.",
    descEn: "Publish and start receiving orders and payments.",
  },
];

export const FAQ_ITEMS = [
  {
    qAr: "ما هو مدار المجرة؟",
    qEn: "What is Madar Al-Majarra?",
    aAr: "منصة SaaS عراقية لإنشاء وإدارة وبيع المتاجر الإلكترونية — من المنتجات والطلبات إلى التقارير والمدفوعات.",
    aEn: "An Iraqi SaaS platform to create, manage, and sell through online stores — from products and orders to reports and payments.",
  },
  {
    qAr: "هل أحتاج خبرة تقنية؟",
    qEn: "Do I need technical skills?",
    aAr: "لا. الواجهة مصممة لتكون بسيطة وواضحة، ويمكنك إطلاق متجرك دون كتابة أي كود.",
    aEn: "No. The interface is built to be clear and simple — launch without writing code.",
  },
  {
    qAr: "كيف أختار الباقة المناسبة؟",
    qEn: "How do I choose a plan?",
    aAr: "ابدأ بالباقة التي تناسب حجم متجرك الحالي، ويمكنك الترقية لاحقاً مع نمو مبيعاتك.",
    aEn: "Start with a plan that fits your current store size, then upgrade as you grow.",
  },
  {
    qAr: "هل تدعمون طرق الدفع العراقية؟",
    qEn: "Do you support Iraqi payments?",
    aAr: "نعم، ندعم بوابات مثل زين كاش وكي كارد لتسهيل الدفع لعملائك داخل العراق.",
    aEn: "Yes — gateways like ZainCash and QiCard to make checkout easy for customers in Iraq.",
  },
  {
    qAr: "هل يمكنني تجربة المنصة قبل الاشتراك؟",
    qEn: "Can I try before subscribing?",
    aAr: "يمكنك إنشاء حساب والاطلاع على الباقات المتاحة، بما في ذلك خيارات تجريبية حسب العرض الحالي.",
    aEn: "You can create an account and explore available plans, including trial options when offered.",
  },
];

export const NAV_LINKS = [
  { id: "why", labelAr: "لماذا مدار؟", labelEn: "Why Madar" },
  { id: "domains", labelAr: "المجالات", labelEn: "Industries" },
  { id: "steps", labelAr: "كيف تبدأ", labelEn: "How it works" },
  { id: "plans-section", labelAr: "الباقات", labelEn: "Pricing" },
  { id: "faq", labelAr: "الأسئلة", labelEn: "FAQ" },
];
